import { cn, focusHaloFlush } from "@pax/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { type Session, sessionKey, signOut } from "@/features/auth/queries";
import { checkSession } from "@/lib/risk";
import { RecaptchaAttribution } from "./RecaptchaAttribution";

/** How long to wait before each retry while Castle can't answer, at most the last. */
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000];

/** Whether a session is still waiting on its sign-in check, in a deployment that checks them. */
export function isCheckPending(session: Session | null | undefined, checksSessions: boolean) {
  return Boolean(session && checksSessions && !session.session.checkedAt);
}

/**
 * The whole window while a new session is pending. It checks the session with Castle and an
 * invisible reCAPTCHA, retrying until Castle answers, then reloads the session so Pax opens.
 */
export function CheckingSession() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const [retries, setRetries] = useState(0);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = async (tries: number) => {
      try {
        await checkSession();
        if (alive) await queryClient.invalidateQueries({ queryKey: sessionKey, exact: true });
      } catch {
        if (!alive) return;
        setRetries(tries + 1);
        const delay = RETRY_DELAYS_MS[Math.min(tries, RETRY_DELAYS_MS.length - 1)];
        timer = setTimeout(() => void attempt(tries + 1), delay);
      }
    };
    void attempt(0);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [queryClient]);

  async function handleSignOut() {
    try {
      await signOut(queryClient);
    } finally {
      await navigate({ to: "/" });
      await router.invalidate();
    }
  }

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-3 bg-background p-6 text-center">
      <Loader2 aria-hidden className="size-6 animate-spin text-muted-foreground" />
      <p className="font-medium" role="status">
        {retries < 2 ? "Checking…" : "Still checking…"}
      </p>
      <p className="max-w-xs text-sm text-muted-foreground">
        {retries < 2
          ? "Making sure you're a person. This takes a moment."
          : "Our checks are slow to answer right now. Pax opens as soon as they do."}
      </p>
      <button
        type="button"
        onClick={() => void handleSignOut()}
        className={cn(
          "mt-4 rounded text-xs text-muted-foreground hover:text-foreground",
          focusHaloFlush,
        )}
      >
        Sign out
      </button>
      <RecaptchaAttribution />
    </div>
  );
}
